import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { ClipGroupModel } from '../types';
import { ClipStatus } from '@/features/clips/types';
import { isUUID } from 'validator';

const ACTIVE_STATUSES: string[] = [
  ClipStatus.NEW,
  ClipStatus.INITIALIZING,
  ClipStatus.PENDING,
  ClipStatus.PROCESSING,
  ClipStatus.CANCELING,
];

export const getQueryKey = (gameId: string, playbyplayId: string) => {
  return ['Games', gameId, 'PlayByPlay', playbyplayId, 'ClipGroups'];
};

interface UsePlayByPlayClipGroupsQueryOptions {
  playbyplayId: string;
  enabled?: boolean;
}

export const usePlayByPlayClipGroupsQuery = ({
  playbyplayId,
  enabled = true
}: UsePlayByPlayClipGroupsQueryOptions) => {
  const gameId = useCurrentGameId();
  const { currentOrg } = useUserStateContext();
  const orgId = (currentOrg as any)?.id;

  const isValidGame = !!gameId && isUUID(gameId);
  const queryKey = getQueryKey(gameId, playbyplayId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!isValidGame || !playbyplayId) {
        return [];
      }
      const params = new URLSearchParams();
      if (orgId) {
        params.append('orgId', String(orgId));
      }
      const response = await get<{ success: boolean; clipGroups: ClipGroupModel[] }>(
        `/v1.0/games/${gameId}/play-by-play/${playbyplayId}/clip-groups${params.toString() ? '?' + params.toString() : ''}`
      );
      return response.data.clipGroups || [];
    },
    enabled: isValidGame && enabled && !!playbyplayId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    // Poll every 3s while any clip is still being processed
    refetchInterval: (query) => {
      const clipGroups = query.state.data;
      if (!clipGroups) return false;
      const hasActiveClips = clipGroups.some((group) =>
        group.clips?.some((clip: { status?: string }) =>
          clip.status && ACTIVE_STATUSES.includes(clip.status.toUpperCase())
        )
      );
      return hasActiveClips ? 3000 : false;
    },
  });

  return {
    ...query,
    clipGroups: query.data || [],
  };
};

export default usePlayByPlayClipGroupsQuery;
